import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AlarmMonitoringDetailOverlayComponent } from '@components/alarm-monitoring-detail-overlay/alarm-monitoring-detail-overlay.component';
import { fadeInOut } from '@helpers/fadeInOut';
import { CssVarService } from '@service/css-vars.service';
import { OpsAlarmMonitoringService } from '@service/ops-alarms/ops-alarm-monitoring.service';
import { PaginationStrategy } from '@service/pagination-strategy.service';
import { ResponsiveService } from '@service/responsive.service';
import { StaticPaginationStrategy } from '@service/static-pagination-strategy';
import { AlarmMonitoring } from 'ops/common/models/alarm-monitoring';
import { Subscription } from 'rxjs';
import { DynamicMaxHeightDirective } from 'src/app/directives/alarm-table-height/alarm-table-height.directive';
import { OpsAlarmChild } from '../ops-alarm.component';

const DEFAULT_ITEMS_PER_PAGE = 10;

@Component({
    selector: 'app-ops-alarm-monitoring',
    templateUrl: './ops-alarm-monitoring.component.html',
    styleUrls: ['./ops-alarm-monitoring.component.scss'],
    animations: [fadeInOut],
    providers: [
        {
            provide: PaginationStrategy,
            useValue: new StaticPaginationStrategy(DEFAULT_ITEMS_PER_PAGE),
        },
    ],
    standalone: false
})
export class OpsAlarmMonitoringComponent implements OnInit, OnDestroy {
    @ViewChild('dynamicMaxHeight') dynamicMaxHeight: DynamicMaxHeightDirective;
    @ViewChild('alarmDetailOverlay') alarmDetailOverlay: AlarmMonitoringDetailOverlayComponent;
    @Output() onBackEmit = new EventEmitter();
    @ViewChild('mainContent') mainContent: ElementRef;

    readonly pageTitle = OpsAlarmChild.Monitoring;

    alarms: AlarmMonitoring[] = [];
    alarmDetail: AlarmMonitoring;
    removeHeightLimits: boolean = false;

    protected _currentPage = 0;

    private _isLoading = false;
    private _isAnyAlarmUnacked = false;
    private updatedAlarmSubscription: Subscription;

    constructor(
        private alarmMonitoringService: OpsAlarmMonitoringService,
        protected paginationStrategy: PaginationStrategy,
        private cssVarService: CssVarService,
        public responsive: ResponsiveService,
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.shouldRemoveHeightLimit();
    }

    ngAfterViewInit(): void {
        const tableRowHeightAlarm = this.cssVarService.tableRowHeightAlarm;
        const tableRowGap = this.cssVarService.tableRowGap;
        this.dynamicMaxHeight.init(tableRowHeightAlarm, tableRowGap, DEFAULT_ITEMS_PER_PAGE);
    }

    ngOnInit(): void {
        this.updateAlarms();
        this.paginationStrategy.recalculate(this.alarms.length);

        this.updatedAlarmSubscription = this.alarmMonitoringService.onAlarmsUpdated$.subscribe(
            () => {
                this.updateAlarms();
            },
        );
        this.shouldRemoveHeightLimit();
    }

    ngOnDestroy(): void {
        this.updatedAlarmSubscription?.unsubscribe();
    }

    updateAlarms() {
        this.alarms = [...this.alarmMonitoringService.getAlarms()];
        this._isAnyAlarmUnacked = this.alarms.some((v) => v.UnAck);
        this.paginationStrategy.recalculate(this.alarms.length);
    }

    handlePageChanged(pageNumber: number) {
        this.setPage(pageNumber);
    }

    setPage(pageNumber: number) {
        this._currentPage = pageNumber;
    }

    onStopBuzzerClick() {
        this.ackAll();
    }

    onAckAllClick() {
        this.alarmMonitoringService
            .ackAllAlarms(this.alarms.reverse().slice(this.pageStart, this.pageEnd))
            .then(() => this.updateAlarms());
    }

    ackAll() {
        this.alarmMonitoringService.clickStopBuzzerData();
    }

    onDetail(alarm: AlarmMonitoring) {
        this.alarmDetail = alarm;
        this.alarmDetailOverlay.setVisible(true);
    }

    displayedPageLimitCount() {
        if (this.responsive.isLarge() || this.responsive.isXLarge()) {
            return 7;
        } else if (this.responsive.isSmall()) {
            return 1;
        } else {
            return 5;
        }
    }

    onBack() {
        this.onBackEmit.emit();
    }

    onScroll() {
        if (this.responsive.isXSmall() || this.responsive.isHandsetLandscape()) {
            const { scrollTop, scrollHeight, clientHeight } = this.mainContent.nativeElement;
            if (scrollTop + clientHeight >= scrollHeight - 5 && !this.isLoading) {
                this.loadMoreItems();
            }
        }
    }

    loadMoreItems() {
        this._currentPage = Math.min(this._currentPage + 1, this.pageCount);
    }

    onTop() {
        this.mainContent.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }

    isScrollBtnVisible() {
        if (this.mainContent) {
            const scrollTop = this.mainContent.nativeElement.scrollTop;
            const topShown = scrollTop > 10;

            return topShown;
        }
        return false;
    }

    shouldRemoveHeightLimit() {
        if (window.innerHeight < 440) {
            this.removeHeightLimits = true;
        } else {
            this.removeHeightLimits = false;
        }
    }

    public shouldDisableButton(alarm: AlarmMonitoring) {
        if (!alarm) {
            return this.alarmMonitoringService.isBuzzerActive() || !this._isAnyAlarmUnacked;
        }
        return (
            this.alarmMonitoringService.isAckInProgress(alarm) ||
            this.alarmMonitoringService.isBuzzerActive() ||
            !alarm.UnAck
        );
    }

    public get isAnyAlarmUnacked() {
        return this._isAnyAlarmUnacked;
    }
    public get isLoading() {
        return this._isLoading;
    }

    public get itemsPerPage() {
        return this.paginationStrategy.getItemsPerPage();
    }
    public get pageCount() {
        return this.paginationStrategy.getPageCount();
    }
    public get pageStart() {
        return !this.responsive.isXSmall() && !this.responsive.isHandsetLandscape()
            ? this.paginationStrategy.getPageStart(this._currentPage)
            : 0;
    }
    public get pageEnd() {
        return this.paginationStrategy.getPageEnd(this._currentPage);
    }

    public get currentPage() {
        return this._currentPage;
    }
    public set currentPage(value: number) {
        this._currentPage = value;
    }
}
